import { PageLayout, Section } from '@/shared/components';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import { NewProjectModal } from '@/features/projects/components/NewProjectModal';
import { ProjectSection } from '@/features/projects/components/ProjectSection';
import { UserAssignmentSchedule, UserAssignmentScheduleRef } from '@/features/projects/components/UserAssignmentSchedule';
import { usePaginatedProjects } from '@/features/projects/hooks/usePaginatedProjects';
import { useOptimizedMasterData } from '@/features/masters/hooks/useOptimizedMasterData';
import { useSupabaseAuth } from '@/shared/hooks/useSupabaseAuth';
import { ProjectWithZones, UserRole } from '@/types';
import { ChevronLeft, ChevronRight, Terminal } from 'lucide-react';
import { Suspense, useCallback, useMemo, useRef } from 'react';

/**
 * ローディングスケルトンコンポーネント
 * データ読み込み中に表示するプレースホルダー
 * 
 * @returns ローディングスケルトン要素
 */
function LoadingSkeleton() {
  return (
    <div className="space-y-6 p-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="border p-4 rounded-lg shadow-md bg-white">
          <div className="flex justify-between items-center mb-3">
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-8 w-20" />
          </div>
          <div className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/6" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * エラー表示コンポーネント
 * エラー発生時に表示するアラート
 * 
 * @param message - 表示するエラーメッセージ
 * @returns エラー表示要素
 */
function ErrorDisplay({ message }: { message: string }) {
  return (
    <div className="p-4">
      <Alert variant="destructive">
        <Terminal className="h-4 w-4" />
        <AlertTitle>エラーが発生しました</AlertTitle>
        <AlertDescription>
          {message}
          <p className="mt-2 text-sm text-muted-foreground">
            問題が解決しない場合は、しばらく時間をおいてから再試行してください。
            または、管理者にお問い合わせください。
          </p>
        </AlertDescription>
      </Alert>
    </div>
  );
}

/**
 * ダッシュボードコンテンツコンポーネント
 * メインダッシュボードの全体的なレイアウトとデータ表示を管理する
 * 
 * @returns ダッシュボードコンテンツコンポーネント
 */
export function DashboardContent() {
  const { user, isLoading: authLoading } = useSupabaseAuth();
  const scheduleRef = useRef<UserAssignmentScheduleRef>(null);

  const {
    projects: inProgressProjects,
    isLoading: projectsLoading,
    error: projectsError,
    pagination,
    setCurrentPage,
    mutate: mutateProjects
  } = usePaginatedProjects({
    status: '進行中',
    include_zones: true
  }, {
    pageSize: 10
  });

  const {
    craftsmen,
    companyVehicles,
    trucks,
    truckDrivers,
    isLoading: masterDataLoading,
    error: masterDataError,
  } = useOptimizedMasterData({
    includeCraftsmen: true,
    includeVehicles: true,
    includeTrucks: true,
    includeDrivers: true,
  });

  const userRole: UserRole = (user?.role as UserRole) || 'USER';
  const currentUserCraftsmanId = user?.craftsman_id || null;
  const isAdmin = userRole?.toUpperCase() === 'ADMIN';

  const scheduleProjectIds = useMemo(() => {
    if (!inProgressProjects || inProgressProjects.length === 0) {
      return [];
    }
    return [];
  }, [inProgressProjects]);

  const isLoading = authLoading || projectsLoading || masterDataLoading;
  const error = projectsError || (masterDataError ? masterDataError.message || 'マスターデータの取得に失敗しました' : null);

  const handleProjectUpdated = useCallback(async () => {
    try {
      await mutateProjects();
    } catch (error) {
      console.error('プロジェクトデータの再取得に失敗:', error);
    }
  }, [mutateProjects]);

  const handleScheduleUpdated = useCallback(async () => {
    try {
      if (scheduleRef.current) {
        await scheduleRef.current.refreshSchedule();
      }
    } catch (error) {
      console.error('スケジュールデータの更新に失敗:', error);
    }
  }, []);


  const projectList = useMemo(() => {
    if (!inProgressProjects || inProgressProjects.length === 0) {
      return (
        <div className="text-center py-8 bg-white rounded-lg border">
          <p className="text-muted-foreground">
            {pagination.currentPage > 1 ? 'このページにはプロジェクトがありません。' : '進行中のプロジェクトはありません。'}
          </p>
          {isAdmin && pagination.currentPage === 1 && (
            <p className="text-sm text-muted-foreground mt-2">
              新しいプロジェクトを作成するには、上部の「新規プロジェクト作成」ボタンをクリックしてください。
            </p>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {inProgressProjects.map((project) => (
          <ProjectSection
            key={project.id}
            project={project as ProjectWithZones}
            craftsmen={craftsmen || []}
            companyVehicles={companyVehicles || []}
            trucks={trucks || []}
            truckDrivers={truckDrivers || []}
            isReadOnly={!isAdmin}
            currentUserCraftsmanId={currentUserCraftsmanId}
            onProjectUpdated={handleProjectUpdated}
            onScheduleUpdated={handleScheduleUpdated}
          />
        ))}
      </div>
    );
  }, [inProgressProjects, craftsmen, companyVehicles, trucks, truckDrivers, currentUserCraftsmanId, isAdmin, handleProjectUpdated, handleScheduleUpdated, pagination.currentPage]);

  if (isLoading) {
    return (
      <PageLayout
        title="ダッシュボード"
        description="進行中のプロジェクトと個人スケジュールを管理する。"
        actions={<Skeleton className="h-10 w-32" />}
      >
        <LoadingSkeleton />
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout
        title="ダッシュボード"
        description="進行中のプロジェクトと個人スケジュールを管理する。"
      >
        <ErrorDisplay message={error.toString()} />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="ダッシュボード"
      description="進行中のプロジェクトと個人スケジュールを管理します。"
      actions={
        <div className="flex gap-2">
          {isAdmin && (
            <NewProjectModal onProjectCreated={handleProjectUpdated}>
              <Button>新規プロジェクト作成</Button>
            </NewProjectModal>
          )}
        </div>
      }
    >

      <div className="space-y-8">
        <Section spacing="md">
          {currentUserCraftsmanId ? (
            <UserAssignmentSchedule
              ref={scheduleRef}
              currentUserCraftsmanId={currentUserCraftsmanId}
              craftsmen={craftsmen || []}
            />
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800">
                個人スケジュールを表示するには、職人として登録されている必要があります。
              </p>
            </div>
          )}
        </Section>

        <Section spacing="md">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-foreground">
              進行中のプロジェクト
              {pagination.totalItems > 0 && (
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  ({(pagination.currentPage - 1) * pagination.pageSize + 1}-{Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems)}/{pagination.totalItems}件)
                </span>
              )}
            </h2>
            {pagination.totalItems > 3 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(pagination.currentPage - 1)}
                  disabled={pagination.currentPage <= 1 || projectsLoading}
                  className="flex items-center gap-1"
                >
                  <ChevronLeft className="h-4 w-4" />
                  前へ
                </Button>
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <span>{pagination.currentPage}</span>
                  <span>/</span>
                  <span>{pagination.totalPages}</span>
                  <span className="text-xs">ページ</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(pagination.currentPage + 1)}
                  disabled={pagination.currentPage >= pagination.totalPages || projectsLoading}
                  className="flex items-center gap-1"
                >
                  次へ
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
          <Suspense fallback={<LoadingSkeleton />}>
            {projectList}
          </Suspense>
        </Section>
      </div>
    </PageLayout>
  );
}